import { Head, useForm } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface Vendor {
  id: number
  display_name: string
}

interface DocumentForm {
  title: string
  document_date: string | null
  notes: string
}

interface Props {
  vendor: Vendor
  document: DocumentForm
  errors?: Record<string, string[]>
}

export default function New({ vendor, document, errors }: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Subcontractors", href: "/company/vendors" },
    { title: vendor.display_name, href: `/company/vendors/${vendor.id}` },
    { title: "Documents", href: `/company/vendors/${vendor.id}/documents` },
    {
      title: "New Document",
      href: `/company/vendors/${vendor.id}/documents/new`,
    },
  ]

  const { data, setData, post, processing, transform } = useForm({
    title: document.title ?? "",
    document_date: document.document_date ?? "",
    notes: document.notes ?? "",
    file: null as File | null,
  })

  transform((data) => ({ vendor_document: data }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post(`/company/vendors/${vendor.id}/documents`, {
      forceFormData: true,
    })
  }

  const inputClass =
    "mt-1 block w-full rounded border border-hpi-light-gray px-3 py-2 focus:border-hpi-active-blue focus:outline-none"
  const labelClass = "block text-sm font-medium text-hpi-dark-gray"

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="New Document" />

      <div className="p-6">
        <h1 className="mb-6 text-2xl font-semibold">New Document</h1>

        <form onSubmit={handleSubmit} className="max-w-2xl space-y-4">
          <div>
            <label className={labelClass}>Title</label>
            <input
              type="text"
              value={data.title}
              onChange={(e) => setData("title", e.target.value)}
              className={inputClass}
            />
            {errors?.title && (
              <p className="text-hpi-red mt-1 text-sm">{errors.title[0]}</p>
            )}
          </div>

          <div>
            <label className={labelClass}>Document Date</label>
            <input
              type="date"
              value={data.document_date ?? ""}
              onChange={(e) => setData("document_date", e.target.value)}
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>File</label>
            <input
              type="file"
              onChange={(e) => setData("file", e.target.files?.[0] ?? null)}
              className="text-hpi-med-gray file:bg-hpi-pale-blue file:text-hpi-dark-blue hover:file:bg-hpi-light-blue mt-1 block w-full text-sm file:mr-4 file:rounded file:border-0 file:px-4 file:py-2 file:text-sm file:font-semibold"
            />
            {errors?.file && (
              <p className="text-hpi-red mt-1 text-sm">{errors.file[0]}</p>
            )}
          </div>

          <div>
            <label className={labelClass}>Notes</label>
            <textarea
              value={data.notes}
              onChange={(e) => setData("notes", e.target.value)}
              rows={4}
              className={inputClass}
            />
          </div>

          <div className="flex justify-end space-x-4">
            <a
              href={`/company/vendors/${vendor.id}/documents`}
              className="border-hpi-light-gray hover:bg-hpi-pale-blue rounded border px-4 py-2"
            >
              Cancel
            </a>
            <button
              type="submit"
              disabled={processing}
              className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white disabled:opacity-50"
            >
              {processing ? "Uploading..." : "Save Document"}
            </button>
          </div>
        </form>
      </div>
    </AppLayout>
  )
}
